// Animation state lives in refs written during render, which React Compiler cannot memoize safely.
"use no memo";

import { useEffect, useRef, useState } from "react";

/** How far behind the source the reveal settles, at any arrival rate. */
const CATCH_UP_MS = 100;

/**
 * Reveals an append-only string a frame at a time instead of in the bursts it
 * arrives in. Each frame drains a fixed fraction of the backlog, so the reveal
 * rate converges on the arrival rate; a fixed characters-per-second rate
 * instead falls further behind the faster the source runs, then snaps at the
 * end of the stream.
 *
 * Text present at mount is already revealed, so opening a view onto a stream
 * already in flight shows what is there rather than replaying it.
 */
export function useStreamReveal(
  text: string,
  streaming: boolean | undefined
): { revealed: string; animating: boolean } {
  const [revealedCount, setRevealedCount] = useState(() => text.length);
  const [animating, setAnimating] = useState(false);
  const revealedRef = useRef(text.length);
  const textRef = useRef(text);
  const streamingRef = useRef(streaming);
  const frameRef = useRef<number | null>(null);
  const lastFrameRef = useRef(0);

  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  textRef.current = text;
  // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
  streamingRef.current = streaming;

  useEffect(() => {
    const stop = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      lastFrameRef.current = 0;
      setAnimating(false);
    };

    // A shorter value is a rewrite, not an append.
    if (revealedRef.current > text.length) {
      revealedRef.current = text.length;
      setRevealedCount(text.length);
    }

    if (revealedRef.current >= text.length) {
      if (!streamingRef.current) stop();
      return;
    }

    if (frameRef.current !== null) return;
    setAnimating(true);

    const tick = (time: number) => {
      if (lastFrameRef.current === 0) lastFrameRef.current = time;
      const elapsed = time - lastFrameRef.current;
      lastFrameRef.current = time;

      const backlog = textRef.current.length - revealedRef.current;
      if (backlog > 0) {
        const step = Math.max(1, Math.round((backlog * elapsed) / CATCH_UP_MS));
        const next = revealedRef.current + Math.min(backlog, step);
        revealedRef.current = next;
        setRevealedCount(next);
      }

      // Stay running while streaming even when caught up: stopping and
      // restarting per burst reintroduces the chop this exists to remove.
      if (
        revealedRef.current < textRef.current.length ||
        streamingRef.current
      ) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        stop();
      }
    };

    frameRef.current = requestAnimationFrame(tick);
  }, [text]);

  useEffect(() => {
    if (streaming !== false) return;
    if (revealedRef.current >= textRef.current.length) return;
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      lastFrameRef.current = 0;
    }
    revealedRef.current = textRef.current.length;
    setRevealedCount(textRef.current.length);
    setAnimating(false);
  }, [streaming]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    []
  );

  return {
    revealed:
      revealedCount >= text.length ? text : text.slice(0, revealedCount),
    animating,
  };
}
